import ExcelJS from 'exceljs';

/**
 * XLSX-выгрузка рассчитанного расхода сырья (вкладка «Передача в 1С»).
 *
 * Формат — сводная «с группировкой по продукту» из пользовательского
 * примера («пример группировки сырья перед выгрузкой.xlsx», правая
 * таблица «надо так»): две колонки — «Названия строк» и «Сумма по полю
 * кол-во сырья на гп»; партии объединяются, строки группируются по
 * продукту ГП (наименование с тарой — одинаковый продукт в разных тарах
 * не склеивается), под продуктом — сырьё, суммированное по наименованию,
 * отдельной строкой «Масса нетто сгустков, кг»; строка продукта — итог
 * по его сырью, замыкает таблицу «Общий итог».
 *
 * Единицы — как в примере: сырьё в граммах (значения пакета в кг × 1000,
 * точность пакета — 1 г), «Масса нетто сгустков, кг» — в кг
 * (пропорционально факту строки выпуска).
 */

export interface OneCExportPackageRow {
    batch_number?: string | null;
    status: string;
    payload?: string | null;
    [key: string]: any;
}

export interface OneCXlsxIngredientRow {
    name: string;
    value: number;
}

export interface OneCXlsxGroup {
    product: string;
    ingredients: OneCXlsxIngredientRow[];
    total: number;
}

const CLOTS_ROW_NAME = 'Масса нетто сгустков, кг';

/** Ключ продукта: наименование + тара (вес в кг, как в примере «тара 3,0кг»). */
export function oneCProductKey(productName: any, container: any): string {
    const name = productName || '(без наименования)';
    const capacity = container && container.capacity_kg;
    if (capacity != null && capacity !== '') {
        // 3 → «3,0», 5.6 → «5,6», 3.05 → «3,05»
        let text = String(capacity).replace('.', ',');
        const [int, frac] = text.split(',');
        if (!frac) {
            text = `${int},0`;
        } else {
            text = `${int},${frac.replace(/0+$/, '') || '0'}`;
        }
        return `${name} (тара ${text}кг)`;
    }
    const containerName = container && container.name;
    return containerName ? `${name} (${containerName})` : name;
}

/**
 * Группировка пакетов по продуктам: Map продукт → Map сырьё → сумма.
 * Пакеты без payload (ошибки расчёта) пропускаются.
 */
export function groupOneCPackages(packages: OneCExportPackageRow[]): OneCXlsxGroup[] {
    const products = new Map<string, Map<string, number>>();

    for (const pkg of packages || []) {
        if (!pkg || !pkg.payload) {
            continue;
        }
        let payload: any;
        try {
            payload = typeof pkg.payload === 'string' ? JSON.parse(pkg.payload) : pkg.payload;
        } catch (e) {
            continue;
        }
        const outputs = (payload && payload.output) || [];

        const batchTotalKg = outputs.reduce(
            (sum: number, o: any) => sum + (parseFloat(o.actual_kg) || 0),
            0,
        );

        for (const output of outputs) {
            const key = oneCProductKey(
                output.product && output.product.name,
                output.container,
            );
            if (!products.has(key)) {
                products.set(key, new Map<string, number>());
            }
            const ingredients = products.get(key)!;

            const lineKg = parseFloat(output.actual_kg) || 0;
            const clotsKg =
                output.journal && parseFloat(output.journal.clots_weight_kg) || 0;
            // масса сгустков партии распределяется по строкам выпуска
            // пропорционально факту (0 при нулевом выпуске партии)
            const clotsShare = batchTotalKg > 0 ? (clotsKg * lineKg) / batchTotalKg : 0;
            ingredients.set(
                CLOTS_ROW_NAME,
                (ingredients.get(CLOTS_ROW_NAME) || 0) + clotsShare,
            );

            for (const material of output.materials || []) {
                const name =
                    material.ingredient && material.ingredient.name
                        ? material.ingredient.name
                        : '(без наименования)';
                const grams = (parseFloat(material.qty_kg) || 0) * 1000;
                ingredients.set(name, (ingredients.get(name) || 0) + grams);
            }
        }
    }

    const groups: OneCXlsxGroup[] = [];
    for (const [product, ingredients] of products) {
        // сгустки с нулевой массой (партия без данных журнала) не выгружаем
        if ((ingredients.get(CLOTS_ROW_NAME) || 0) === 0) {
            ingredients.delete(CLOTS_ROW_NAME);
        }
        const rows = Array.from(ingredients.entries())
            .map(([name, value]) => ({name, value}))
            .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
        // пустые группы (нулевой выпуск/нулевая тара) в выгрузку не попадают
        const total = rows.reduce((sum, row) => sum + row.value, 0);
        if (total === 0 && rows.every((row) => row.value === 0)) {
            continue;
        }
        groups.push({
            product,
            ingredients: rows,
            total,
        });
    }
    groups.sort((a, b) => a.product.localeCompare(b.product, 'ru'));
    return groups;
}

export function onecGrandTotal(groups: OneCXlsxGroup[]): number {
    return (groups || []).reduce((sum, group) => sum + group.total, 0);
}

const HEADER_ROW_LABEL = 'Названия строк';
const HEADER_VALUE_LABEL = 'Сумма по полю кол-во сырья на гп';

function applyAmountFormat(cell: ExcelJS.Cell) {
    // числовой формат как в примере (numFmtId 4)
    cell.numFmt = '#,##0.00';
}

/** Книга «Расчет расхода сырья по продуктам» — лист в две колонки, как в примере. */
export function createOneCExportWorkbook(packages: OneCExportPackageRow[]): ExcelJS.Workbook {
    const wb = new ExcelJS.Workbook();
    const sheet = wb.addWorksheet('Расчет расхода сырья', {
        pageSetup: {fitToPage: true, fitToWidth: 1, fitToHeight: 0},
    });

    sheet.getColumn(1).width = 62;
    sheet.getColumn(2).width = 18;

    const title = sheet.getRow(1);
    title.getCell(1).value = 'Расчет расхода сырья по продуктам';
    title.getCell(1).font = {bold: true, size: 12};
    title.height = 22;

    const header = sheet.getRow(3);
    header.getCell(1).value = HEADER_ROW_LABEL;
    header.getCell(2).value = HEADER_VALUE_LABEL;
    header.eachCell((cell) => {
        cell.font = {bold: true};
        cell.border = {
            bottom: {style: 'thin'},
        };
    });

    const groups = groupOneCPackages(packages);
    let rowNumber = 4;
    for (const group of groups) {
        const productRow = sheet.getRow(rowNumber++);
        productRow.getCell(1).value = group.product;
        productRow.getCell(2).value = group.total;
        productRow.getCell(1).font = {bold: true};
        productRow.getCell(2).font = {bold: true};
        applyAmountFormat(productRow.getCell(2));

        for (const ingredient of group.ingredients) {
            const row = sheet.getRow(rowNumber++);
            row.getCell(1).value = ingredient.name;
            row.getCell(2).value = ingredient.value;
            row.getCell(1).alignment = {indent: 2};
            applyAmountFormat(row.getCell(2));
        }
    }

    const totalRow = sheet.getRow(rowNumber);
    totalRow.getCell(1).value = 'Общий итог';
    totalRow.getCell(2).value = onecGrandTotal(groups);
    totalRow.getCell(1).font = {bold: true};
    totalRow.getCell(2).font = {bold: true};
    totalRow.getCell(1).border = {top: {style: 'thin'}};
    totalRow.getCell(2).border = {top: {style: 'thin'}};
    applyAmountFormat(totalRow.getCell(2));

    return wb;
}

/** Имя файла выгрузки одной партии. */
export function onecBatchFilename(batchNumber: any): string {
    const safe = String(batchNumber || 'бн').replace(/[^\wа-яА-ЯёЁ.-]+/g, '_');
    return `расход_сырья_партия_${safe}.xlsx`;
}

/** Имя файла выгрузки всей смены. */
export function onecShiftFilename(shiftDate: any, shiftId: any): string {
    const safe = String(shiftDate || shiftId || 'смена').replace(/[^\wа-яА-ЯёЁ.-]+/g, '_');
    return `расход_сырья_смена_${safe}.xlsx`;
}
